import { useMemo, useState } from 'react'
import { Link } from 'react-router'
import { Plus, Building2, MoreHorizontal } from 'lucide-react'
import {
  Button,
  EntityListShell,
  PageHeader,
  Pagination,
  SearchInput,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Spinner,
  StatusChip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Dialog,
  DialogFooter,
  Label,
  Input,
  PlacesAddressAutocomplete,
  toast,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  formatDate,
} from '@rr/ui'
import type { PlaceDetailsResolved } from '@rr/ui'
import {
  usePlatformCompaniesList,
  useCreatePlatformCompany,
  useActivatePlatformCompany,
  useDeactivatePlatformCompany,
} from '@/modules/platform/hooks/usePlatformCompanies'
import { useApi } from '@/lib/hooks/use-api'

const COUNTRIES = ['TM', 'KZ', 'UZ', 'AZ', 'GE']
const PAGE_SIZE = 10

type CompanyStatus = 'active' | 'inactive' | 'pending'

export default function CompaniesPage() {
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState<'all' | CompanyStatus>('all')
  const [country, setCountry] = useState<'all' | string>('all')
  const [page, setPage] = useState(1)
  const [creating, setCreating] = useState(false)

  // Server-side filters; the platform endpoint accepts whatever it accepts.
  // We only forward the ones we know about; the rest are filtered client-side.
  const filters = useMemo(
    () => ({
      page,
      limit: PAGE_SIZE,
      search: search.trim() || undefined,
      status: status !== 'all' ? status : undefined,
      country: country !== 'all' ? country : undefined,
    }),
    [page, search, status, country]
  )

  const { data, isLoading, isError, error } = usePlatformCompaniesList(filters)
  const activateMutation = useActivatePlatformCompany()
  const deactivateMutation = useDeactivatePlatformCompany()

  const items = data?.data?.items ?? []
  const total = data?.data?.pagination?.total ?? items.length

  return (
    <div className="page-pad">
      <div className="max-w-7xl mx-auto space-y-6">
        <PageHeader
          title="Organizations"
          subtitle="All tenants on the Route N Route platform."
          actions={
            <Button onClick={() => setCreating(true)}>
              <Plus className="w-4 h-4" /> Create organization
            </Button>
          }
        />

        <EntityListShell
          toolbar={
            <>
              <SearchInput
                value={search}
                onChange={(v) => { setSearch(v); setPage(1) }}
                placeholder="Search companies, domains, industries…"
                className="max-w-sm"
              />
              <Select value={status} onValueChange={(v) => { setStatus(v as typeof status); setPage(1) }}>
                <SelectTrigger className="w-[160px]"><SelectValue placeholder="Status" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
              <Select value={country} onValueChange={(v) => { setCountry(v); setPage(1) }}>
                <SelectTrigger className="w-[140px]"><SelectValue placeholder="Country" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All countries</SelectItem>
                  {COUNTRIES.map((c) => (<SelectItem key={c} value={c}>{c}</SelectItem>))}
                </SelectContent>
              </Select>
            </>
          }
          empty={!isLoading && items.length === 0}
          emptyState={isError ? (error instanceof Error ? error.message : 'Failed to load companies.') : 'No companies match your filters.'}
          pagination={
            <Pagination
              page={page}
              pageSize={PAGE_SIZE}
              total={total}
              onChange={(p) => setPage(p)}
            />
          }
        >
          {isLoading ? (
            <div className="flex items-center justify-center py-16">
              <Spinner />
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Company</TableHead>
                  <TableHead>Country</TableHead>
                  <TableHead>Industry</TableHead>
                  <TableHead>Users</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="w-12" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((c: any) => {
                  const id = String(c.id)
                  const name = c.name ?? c.legalName ?? '—'
                  const domain = c.domain ?? c.website ?? ''
                  const cStatus = (c.status ?? 'active') as CompanyStatus
                  return (
                    <TableRow key={id}>
                      <TableCell>
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="w-8 h-8 rounded-lg bg-muted grid place-items-center text-muted-foreground shrink-0">
                            <Building2 className="w-4 h-4" />
                          </div>
                          <div className="min-w-0">
                            <Link to={`/tenants/organizations/${id}`} className="font-medium text-foreground hover:underline block truncate">
                              {name}
                            </Link>
                            {domain && <p className="text-xs text-muted-foreground truncate">{domain}</p>}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="text-sm">{c.country ?? c.countryCode ?? '—'}</TableCell>
                      <TableCell className="text-sm">{c.industry ?? '—'}</TableCell>
                      <TableCell className="tnum">{c.usersCount ?? c.users ?? '—'}</TableCell>
                      <TableCell><StatusChip status={cStatus} /></TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {formatDate(c.createdAt)}
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" aria-label="Row actions">
                              <MoreHorizontal className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem asChild>
                              <Link to={`/tenants/organizations/${id}`}>Open detail</Link>
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => toast(`Impersonate ${name} — not wired yet`, 'info')}>
                              Impersonate
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={async () => {
                                try {
                                  if (cStatus === 'active') {
                                    await deactivateMutation.mutateAsync(id)
                                    toast(`Deactivated ${name}`, 'success')
                                  } else {
                                    await activateMutation.mutateAsync(id)
                                    toast(`Activated ${name}`, 'success')
                                  }
                                } catch (e) {
                                  toast(e instanceof Error ? e.message : 'Failed to update status', 'error')
                                }
                              }}
                            >
                              {cStatus === 'active' ? 'Deactivate' : 'Activate'}
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          )}
        </EntityListShell>
      </div>

      <CreateCompanyDialog
        open={creating}
        onClose={() => setCreating(false)}
      />
    </div>
  )
}

function CreateCompanyDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [name, setName] = useState('')
  const [domain, setDomain] = useState('')
  const [industry, setIndustry] = useState('')
  const [contactEmail, setContactEmail] = useState('')
  const [contactPhone, setContactPhone] = useState('')
  const [place, setPlace] = useState<PlaceDetailsResolved | null>(null)

  const createMutation = useCreatePlatformCompany()
  const api = useApi()

  const fetchSuggestions = async (input: string) => {
    const res = await api.platform.getPlacesAutocomplete({ input })
    return ((res as any)?.data ?? []) as Array<{
      id: string
      mainText: string
      secondaryText?: string | null
      description: string
    }>
  }

  const fetchDetails = async (placeId: string) => {
    const res = await api.platform.getPlaceDetails(placeId)
    return (res as any)?.data as PlaceDetailsResolved
  }

  function reset() {
    setName('')
    setDomain('')
    setIndustry('')
    setContactEmail('')
    setContactPhone('')
    setPlace(null)
  }
  function handleClose() {
    reset()
    onClose()
  }

  const hasResolvedAddress =
    !!place?.city && !!place?.countryId
  const submitDisabled =
    createMutation.isPending || !name.trim() || !hasResolvedAddress

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      title="Create organization"
      subtitle="Each organization gets its own users, fleet, clients, and reference data."
      maxWidth="md"
    >
      <form
        id="create-org-form"
        onSubmit={async (e) => {
          e.preventDefault()
          if (submitDisabled || !place) return
          try {
            await createMutation.mutateAsync({
              name: name.trim(),
              domain: domain.trim() || undefined,
              industry: industry.trim() || undefined,
              contactEmail: contactEmail.trim() || undefined,
              contactPhone: contactPhone.trim() || undefined,
              address: {
                city: place.city ?? '',
                countryId: place.countryId ?? '',
                address: place.formattedAddress ?? undefined,
                state: place.state ?? undefined,
                zip: place.zip ?? undefined,
                latitude: place.latitude ?? undefined,
                longitude: place.longitude ?? undefined,
              },
            })
            toast(`Organization "${name.trim()}" created`, 'success')
            handleClose()
          } catch (err) {
            toast(err instanceof Error ? err.message : 'Failed to create organization', 'error')
          }
        }}
        className="grid gap-4"
      >
        <div className="grid gap-1.5">
          <Label htmlFor="co-name">Name</Label>
          <Input
            id="co-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Caspian Imports"
            required
          />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="grid gap-1.5">
            <Label htmlFor="co-domain">Domain</Label>
            <Input
              id="co-domain"
              value={domain}
              onChange={(e) => setDomain(e.target.value)}
              placeholder="caspian-imp.com"
            />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="co-industry">Industry</Label>
            <Input
              id="co-industry"
              value={industry}
              onChange={(e) => setIndustry(e.target.value)}
              placeholder="Logistics"
            />
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="grid gap-1.5">
            <Label htmlFor="co-email">Contact email</Label>
            <Input
              id="co-email"
              type="email"
              value={contactEmail}
              onChange={(e) => setContactEmail(e.target.value)}
              placeholder="ops@caspian-imp.com"
            />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="co-phone">Contact phone</Label>
            <Input
              id="co-phone"
              value={contactPhone}
              onChange={(e) => setContactPhone(e.target.value)}
              placeholder="+993..."
            />
          </div>
        </div>

        <PlacesAddressAutocomplete
          label="Headquarters address"
          placeholder="Start typing — city or full address"
          value={place ?? null}
          onChange={setPlace}
          fetchSuggestions={fetchSuggestions}
          fetchDetails={fetchDetails}
          required
        />

        <DialogFooter>
          <Button type="button" variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button type="submit" form="create-org-form" disabled={submitDisabled}>
            {createMutation.isPending ? 'Creating…' : 'Create organization'}
          </Button>
        </DialogFooter>
      </form>
    </Dialog>
  )
}
